import { useCallback, useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { useSelectedLanguageContext } from '~/components/SelectedLanguage';
import {
  CollaborationWorkList,
  useGetCollaborationWorkLists,
} from '~/data/collaboration';
import { useGetEmployeeCollaborationWorkLists } from '~/data/collaboration/useGetEmployeeCollaborationWorkLists';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  WorkListTableRow,
  convertCollaborationTaskListsToWorkListTableRows,
  createNewTaskFormData,
  workListRoleAccess,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import { WorklistTaskFormData } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';

export const useWorklistTable = () => {
  const {
    selectedEmployeeId,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const canAddWorkList = useMemo(() => {
    return hasRoleAccessOverEmployee(workListRoleAccess.canCreateWorkList);
  }, [hasRoleAccessOverEmployee]);
  const loggedInEmployeeId = useLoggedInEmployeeId();

  const { language } = useSelectedLanguageContext();

  const { getEnums } = useAllEnums();
  const { taskStateEnums, taskTypeEnums } = useMemo(() => {
    return {
      taskStateEnums: getEnums(EnumListKey.COLLAB_TASK_STATUS),
      taskTypeEnums: getEnums(EnumListKey.COLLAB_TASK_TYPE),
    };
  }, [getEnums]);

  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
  };

  const { data: allWorkLists, isLoading: isLoadingAllWorkLists } =
    useGetCollaborationWorkLists({ language, includeAll: true }, swrConfig);

  const {
    data: employeeWorkLists,
    mutate,
    isLoading,
  } = useGetEmployeeCollaborationWorkLists(
    { employeeId: selectedEmployeeId, language },
    swrConfig,
  );

  const tableRows: WorkListTableRow[] =
    convertCollaborationTaskListsToWorkListTableRows({
      employeeWorkLists,
      allWorkLists,
      taskStateEnums,
      taskTypeEnums,
    });

  const getNewTaskFormData = useCallback(
    (props: {
      templateId?: number;
      worklistId?: string;
      seq?: number;
    }): WorklistTaskFormData | undefined => {
      const { templateId, worklistId, seq } = props;
      if (!templateId || !worklistId) {
        return undefined;
      }

      const template = allWorkLists.find(
        (wl) => wl.fCollaborationTemplateId === templateId,
      );

      return createNewTaskFormData({
        taskType: template?.fCollaborationTemplateTypeFK ?? '',
        creatorEmployeeId: loggedInEmployeeId,
        targetEmployeeId: selectedEmployeeId,
        worklistId,
        seq,
      });
    },
    [allWorkLists, loggedInEmployeeId, selectedEmployeeId],
  );

  const workListOptions: CollaborationWorkList[] = useMemo(() => {
    return allWorkLists.filter((wl) => wl.fCollaborationTemplateIsActive);
  }, [allWorkLists]);

  return {
    workListOptions,
    canAddWorkList,
    isLoading: isLoading || isLoadingAllWorkLists,
    employeeId: selectedEmployeeId,
    rows: tableRows,
    onUpdate: () => {
      mutate();
    },
    getNewTaskFormData,
  };
};
